import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Search, Crosshair, BarChart3, Eye, FileText, Camera, Tag, Wand2, Repeat, MessageSquare, Handshake, ShieldAlert, Package, Boxes, Share2,
  TrendingUp, LayoutDashboard, Crown, ListChecks, Sunrise, Radar, Bell, Brain, GraduationCap, Bot, Terminal, Plug, ListOrdered, ScrollText, Settings,
  ShieldCheck, Menu, X, LogOut, Sparkles, Home, Gift, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cx, ApiLoadStatus } from "@/components/kit";

import ThemeToggle from "@/components/ThemeToggle";
import "@/theme.css";

import { SuiteNav } from "@/components/ProfimySuite";

export const NAV = [
  ["DÉCOUVRIR", [["/scout", "Scout", Crosshair], ["/market", "Market", BarChart3], ["/competitor", "Competitor", Eye]]],
  ["VENDRE", [["/listing", "Listing", FileText], ["/photo", "Photo", Camera], ["/price", "Price", Tag], ["/optimizer", "Optimizer", Wand2], ["/reposter", "Reposter", Repeat]]],
  ["CONVERSATIONS", [["/messenger", "Messenger", MessageSquare], ["/negotiator", "Negotiator", Handshake], ["/anti-scam", "Anti-Scam", ShieldAlert]]],
  ["OPÉRATIONS", [["/orders", "Orders", Package], ["/stock", "Stock", Boxes], ["/crossposter", "Crossposter", Share2]]],
  ["PERFORMANCE", [["/profitability", "Profitability", TrendingUp], ["/dashboard", "Dashboard", LayoutDashboard], ["/ceo", "CEO", Crown]]],
  ["AGENT", [["/daily-plan", "Daily Plan", ListChecks], ["/morning-brief", "Morning Brief", Sunrise], ["/watchdog", "Watchdog", Radar], ["/alerts", "Alerts", Bell],
    ["/memory", "Memory", Brain], ["/learning", "Learning", GraduationCap], ["/autopilot", "Autopilot", Bot]]],
  ["SYSTÈME", [["/connectors", "Connecteurs", Plug], ["/queue", "File d'actions", ListOrdered], ["/audit", "Audit log", ScrollText], ["/settings", "Paramètres", Settings]]],
  ["CROISSANCE", [["/parrainage", "Parrainage", Gift]]],
];

function Sidebar({ onNav, isAdmin }) {
  return (
    <nav className="flex flex-col gap-5 p-4 overflow-y-auto h-full" data-testid="sidebar">
      <NavLink to="/" onClick={onNav} className="flex items-center gap-2 px-2" data-testid="nav-home-logo">
        <div className="w-8 h-8 rounded-lg bg-[#7CFF00] text-black grid place-items-center font-black font-display">L</div>
        <div className="font-display font-black tracking-tight text-lg">LBC<span className="text-[#7CFF00]"> Agent</span></div>
      </NavLink>
      <NavLink to="/command" onClick={onNav} data-testid="nav-command-center"
        className={({ isActive }) => cx("flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold glow", isActive ? "bg-[#7CFF00] text-black" : "text-[#7CFF00]")}>
        <Terminal size={16} /> Command Center
      </NavLink>
      <Item to="/" label="Accueil" Icon={Home} onNav={onNav} end />
      <NavLink to="/tutoriel" onClick={onNav} data-testid="nav-tutoriel"
        className={({ isActive }) => cx("flex items-center gap-3 rounded-xl border px-3 py-3 text-base font-bold transition-colors", isActive ? "bg-[#7CFF00] border-[#7CFF00] text-black" : "bg-[#7CFF00]/10 border-[#7CFF00]/40 text-[#C5FF91] hover:bg-[#7CFF00]/20")}>
        <PlayCircle size={23} aria-hidden="true" /> <span>Tutoriel vidéo</span>
      </NavLink>
      <SuiteNav current="lbc-agent" home="/" onNavigate={onNav} />
      {NAV.map(([group, items]) => (
        <div key={group}>
          <div className="mono text-[10px] tracking-[.25em] text-[#626E77] px-3 mb-1.5">{group}</div>
          {items.map(([to, label, Icon]) => <Item key={to} to={to} label={label} Icon={Icon} onNav={onNav} />)}
        </div>
      ))}
      {isAdmin && <Item to="/admin" label="Admin" Icon={ShieldCheck} onNav={onNav} />}
    </nav>
  );
}

const Item = ({ to, label, Icon, onNav, end }) => (
  <NavLink to={to} end={end} onClick={onNav} data-testid={`nav-${to.replace("/", "") || "home"}`}
    className={({ isActive }) => cx("flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors", isActive ? "bg-[#7CFF00]/10 text-[#7CFF00]" : "text-[#A0AAB2] hover:text-white hover:bg-white/[.03]")}>
    <Icon size={15} /> {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();
  const demo = user?.workspace?.is_demo;
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); nav("/command"); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [nav]);
  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/command?q=${encodeURIComponent(q)}`); setQ(""); } };
  return (
    <div className="lbc-app min-h-screen flex bg-black">
      <aside className="hidden lg:block w-64 shrink-0 border-r border-[rgba(124,255,0,.12)] h-screen sticky top-0 bg-[#050605]"><Sidebar isAdmin={user?.role === "admin"} /></aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#050605] border-r border-[rgba(124,255,0,.15)]"><Sidebar onNav={() => setOpen(false)} isAdmin={user?.role === "admin"} /></aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 glass border-x-0 border-t-0 px-4 lg:px-8 py-3 flex items-center gap-3">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></button>
          {loc.pathname !== "/command" && (
            <form onSubmit={submit} className="flex-1 max-w-2xl relative" data-testid="topbar-command-form">
              <Sparkles size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7CFF00]" />
              <input data-testid="topbar-command-input" value={q} onChange={(e) => setQ(e.target.value)} className="inp pl-9 rounded-full" placeholder="Que veux-tu que LBC Agent fasse ?  (Ctrl+K)" />
            </form>
          )}
          <div className="flex-1" />
          <ThemeToggle />
          {demo && <span data-testid="demo-data-badge" className="mono text-[10px] tracking-widest px-2 py-1 rounded border border-[#FFBD36] text-[#FFBD36] bg-[#FFBD36]/10">DEMO DATA</span>}
          <span className="hidden sm:inline text-sm text-[#A0AAB2]" data-testid="topbar-user">{user?.first_name}</span>
          <button onClick={logout} className="text-[#A0AAB2] hover:text-[#FF453A]" data-testid="logout-btn" title="Déconnexion"><LogOut size={17} /></button>
        </header>
        <main className="px-4 lg:px-8 py-8 max-w-[1500px] mx-auto"><ApiLoadStatus /><Outlet /></main>
      </div>
      {open && <button className="fixed top-3 right-3 z-[60] lg:hidden text-white" onClick={() => setOpen(false)}><X /></button>}
    </div>
  );
}
